import React from "react";
import { LockOpenRounded as LockOpenIcon } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { useMutation } from "@apollo/client";
import { t } from "i18next";
import { REOPEN_RESPONSE } from "../../Queries";
import { useErrorBoundary } from "../../Contexts/ErrorContext";
import { User, Response, CampaignState } from "../../types";

export interface Props {
  user: User;
  responses: Response[];
  responseAuthors: User[];
  campaignState: CampaignState;
}

// FL-344: a response is still open to its author when it hasn't been
// submitted and the survey is either running or reopened for them (the same
// rule UserResponse uses to decide whether to show the form).
const SURVEY_CLOSED_STATES = [CampaignState.Closed, CampaignState.Archived];

export function isResponseOpen(
  response: Response,
  campaignState: CampaignState,
): boolean {
  if (response.submitted) return false;
  return response.reopened || !SURVEY_CLOSED_STATES.includes(campaignState);
}

// FL-84: instructor-only button to reopen an individual student's response
// for further modification — revise a completed response, or grant access on
// a survey that has closed. Placed to the left of the RespondButton.
export const ReopenButton = ({
  responses,
  responseAuthors,
  user,
  campaignState,
}: Props) => {
  const { setError } = useErrorBoundary();

  const [reopenResponse, { loading }] = useMutation(REOPEN_RESPONSE, {
    refetchQueries: ["GetResponsesSummary"],
    onError: (error) => setError(t("errors.responses.reopen"), error),
  });

  const response = responses.find((r) => user.id === r.authorId);
  const alreadyOpen =
    response !== undefined && isResponseOpen(response, campaignState);
  const disabled =
    !responseAuthors.includes(user) || !response || alreadyOpen || loading;

  function handleClick() {
    if (response) {
      reopenResponse({ variables: { responseId: response.id } });
    }
  }

  return (
    <Tooltip
      title={
        alreadyOpen
          ? t("surveys.reopenResponse.alreadyOpen")
          : disabled
            ? ""
            : t("surveys.reopenResponse.tooltip")
      }
    >
      {/* The disabled button drops out of the tab order; the tooltip anchor
          (its wrapper) takes its place so keyboard users get the reason
          (WCAG 2.1.1 — same pattern as the question editor's Finish
          Editing button). */}
      <span tabIndex={alreadyOpen ? 0 : undefined}>
        <IconButton
          edge="end"
          aria-label="reopen response"
          disabled={disabled}
          onClick={handleClick}
        >
          <LockOpenIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
};
